import {
  type GetApiPolicyQueryParams,
  type UpdatePolicyRequestModel,
} from '@/shared/api'
import { createEvent, sample } from 'effector'
import { createGate } from 'effector-react'

import {
  documentCharterQuery,
  documentFacebookProfileDeletionGuideQuery,
  documentOfferQuery,
  documentPolicyQuery,
  documentTermsQuery,
  updateDocument,
} from '../api'
import { POLICY_LANGUAGE_CODE } from '../lib/constants'
import { UserPolicyTypeEnum } from '../lib/policy-types'

export const SettingsDocumentsGate = createGate()

const policyQueryCall = createEvent()
const termsQueryCall = createEvent()
const facebookProfileDeletionGuideQueryCall = createEvent()
const offerQueryCall = createEvent()
const charterQueryCall = createEvent()

sample({
  clock: policyQueryCall,
  fn: (): { params: GetApiPolicyQueryParams } => ({
    params: {
      LanguageCode: POLICY_LANGUAGE_CODE,
      Type: UserPolicyTypeEnum.PrivacyPolicy,
    },
  }),
  target: documentPolicyQuery.start,
})

sample({
  clock: termsQueryCall,
  fn: () => ({
    params: {
      LanguageCode: POLICY_LANGUAGE_CODE,
      Type: UserPolicyTypeEnum.TermsOfUse,
    },
  }),
  target: documentTermsQuery.start,
})

sample({
  clock: facebookProfileDeletionGuideQueryCall,
  fn: () => ({
    params: {
      LanguageCode: POLICY_LANGUAGE_CODE,
      Type: UserPolicyTypeEnum.FacebookProfileDeletionGuide,
    },
  }),
  target: documentFacebookProfileDeletionGuideQuery.start,
})

sample({
  clock: offerQueryCall,
  fn: () => ({
    params: {
      LanguageCode: POLICY_LANGUAGE_CODE,
      Type: UserPolicyTypeEnum.Offer,
    },
  }),
  target: documentOfferQuery.start,
})

sample({
  clock: charterQueryCall,
  fn: () => ({
    params: {
      LanguageCode: POLICY_LANGUAGE_CODE,
      Type: UserPolicyTypeEnum.Charter,
    },
  }),
  target: documentCharterQuery.start,
})

sample({
  clock: SettingsDocumentsGate.open,
  target: [
    policyQueryCall,
    termsQueryCall,
    facebookProfileDeletionGuideQueryCall,
    offerQueryCall,
    charterQueryCall,
  ],
})

sample({
  clock: SettingsDocumentsGate.close,
  target: [
    documentPolicyQuery.reset,
    documentTermsQuery.reset,
    documentFacebookProfileDeletionGuideQuery.reset,
    documentOfferQuery.reset,
    documentCharterQuery.reset,
  ],
})

export const termsSubmit = createEvent<{ data: UpdatePolicyRequestModel }>()

sample({
  source: documentTermsQuery.$data,
  clock: termsSubmit,
  filter: (data, submitData) =>
    !data || !data.file || data?.file.id !== submitData.data.fileId,
  fn: (_, submitData) => submitData,
  target: updateDocument.start,
})

export const policySubmit = createEvent<{ data: UpdatePolicyRequestModel }>()

sample({
  source: documentPolicyQuery.$data,
  clock: policySubmit,
  filter: (data, submitData) =>
    !data || !data.file || data?.file.id !== submitData.data.fileId,
  fn: (_, submitData) => submitData,
  target: updateDocument.start,
})

export const facebookProfileDeletionGuideSubmit = createEvent<{
  data: UpdatePolicyRequestModel
}>()

sample({
  source: documentFacebookProfileDeletionGuideQuery.$data,
  clock: facebookProfileDeletionGuideSubmit,
  filter: (data, submitData) =>
    !data || !data.file || data?.file.id !== submitData.data.fileId,
  fn: (_, submitData) => submitData,
  target: updateDocument.start,
})

export const offerSubmit = createEvent<{ data: UpdatePolicyRequestModel }>()

sample({
  source: documentOfferQuery.$data,
  clock: offerSubmit,
  filter: (data, submitData) =>
    !data || !data.file || data?.file.id !== submitData.data.fileId,
  fn: (_, submitData) => submitData,
  target: updateDocument.start,
})

export const charterSubmit = createEvent<{ data: UpdatePolicyRequestModel }>()

sample({
  source: documentCharterQuery.$data,
  clock: charterSubmit,
  filter: (data, submitData) =>
    !data || !data.file || data?.file.id !== submitData.data.fileId,
  fn: (_, submitData) => submitData,
  target: updateDocument.start,
})
